import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { signOut } from 'next-auth/react';
import { toast } from 'react-toastify';

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  retry?: boolean;
}

// import token from '@/utils/token';
// import { refreshToken as refreshTokenApi } from '@/services/api/auth';

// interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
//     retry?: boolean;
// }

const authApi = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_ENDPOINT,
  headers: {
    'Content-Type': 'application/json',
  },
  // Prevent axios from trying to interpret date strings as Date objects
  transformRequest: [
    function (data, headers) {
      // Handle stringified dates
      const stringifyDate = (obj: any) => {
        if (!obj) return obj;

        Object.keys(obj).forEach(key => {
          // If object property is an array, process each item
          if (Array.isArray(obj[key])) {
            obj[key].forEach((item: any) => {
              if (typeof item === 'object' && item !== null) {
                stringifyDate(item);
              }
            });
          }
          // If property is an object (but not a Date), recurse
          else if (typeof obj[key] === 'object' && obj[key] !== null && !(obj[key] instanceof Date)) {
            stringifyDate(obj[key]);
          }
          // If property is a Date object, convert to ISO string with full date and time
          else if (obj[key] instanceof Date) {
            obj[key] = obj[key].toISOString();
          }
        });

        return obj;
      };

      // Process the data
      if (data) {
        data = stringifyDate(data);
        return JSON.stringify(data);
      }

      return data;
    }
  ]
});


const saveTokenToLocalStorage = (token: string, refreshToken: string) => {
  // exprise in 1 day
  const expiresAt = new Date().getTime() + 24 * 60 * 60 * 1000;
  localStorage.setItem('accessToken', token);
  localStorage.setItem('refreshToken', refreshToken);
  localStorage.setItem('expiresAt', expiresAt.toString());
};


export const removeTokenFromLocalStorage = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('expiresAt');
};

const getTokenFromLocalStorage = () => {
  const accessToken = localStorage.getItem('accessToken');
  const refreshToken = localStorage.getItem('refreshToken');
  const expiresAt = localStorage.getItem('expiresAt');

  if (!accessToken || !expiresAt) {
    return { accessToken: null, refreshToken: null };
  }
  const now = new Date().getTime();
  if (now > parseInt(expiresAt)) {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('expiresAt');
    return { accessToken: null, refreshToken: null };
  }
  return { accessToken, refreshToken };
};

// New function to get the access token
export const getAccessToken = async (isForce: boolean = false): Promise<{ accessToken: string | null, refreshToken: string | null }> => {
  try {
    if (!isForce) {
      const { accessToken, refreshToken: refreshTokenLocal } = getTokenFromLocalStorage();
      if (accessToken && refreshTokenLocal) {
        return { accessToken, refreshToken: refreshTokenLocal };
      }
    }
    const response = await fetch('/api/auth/session');
    const session = await response.json();
    console.log("session",session)
    const token = session?.accessToken || null;
    const refreshToken = session?.user?.refreshToken;
    if (token) {
      saveTokenToLocalStorage(token, refreshToken);
    }
    return { accessToken: token, refreshToken };
  } catch (error) {
    console.error('Error fetching access token:', error);
    return { accessToken: null, refreshToken: null };
  }
};

interface RequestParams {
  path: string;
  body?: object;
  params?: object;
  method: AxiosRequestConfig['method'];
  isShowError?: boolean;
  isRequireToken?: boolean;
  isWithRefresh?: boolean;
}

export const makeAuthRequest = async ({ method, path, body, params, isShowError = true, isRequireToken = false, isWithRefresh = false }: RequestParams) => {
  const { accessToken, refreshToken } = await getAccessToken(isWithRefresh);

  if (isRequireToken && !accessToken) {
    return Promise.reject(new Error('No access token found'));
  }

  const config = {
    method,
    url: path,
    data: body,
    params: {
      ...params,
      ...(isWithRefresh && { refreshToken }),
    },
    headers: {
      'Content-Type': 'application/json',
      Authorization: accessToken ? `Bearer ${accessToken}` : '',
    },
    isRequireToken,
  };

  try {
    const response = await authApi(config);
    return response.data;
  } catch (error) {
    if (isShowError) {
      handleError(error);
    }
    throw error;
  }
};

// Lưu trữ các thông báo lỗi đã hiển thị gần đây để tránh hiển thị lại
const recentErrors = new Map();
const ERROR_COOLDOWN = 3000; // 3 giây cooldown giữa các lỗi giống nhau

export const handleError = (error: any) => {
  if (error?.errorCode === "CO02") return;

  let errorMessage = '';
  
  if (error.response?.data?.message) {
    errorMessage = error.response.data.message;
  } else if (error?.code === 'ERR_NETWORK') {
    errorMessage = 'An unexpected error occurred.';
  } else if (error.response) {
    // The request was made and the server responded with a status code that falls out of the range of 2xx
    switch (error.response.status) {
      // case 400:
      // return toast.error('Bad Request: The server could not understand the request.');
      case 401:
        errorMessage = 'Unauthorized: Authentication failed or user does not have permissions.';
        break;
      case 403:
        errorMessage = 'Forbidden: You do not have access to this resource.';
        break;
      case 413:
        errorMessage = 'Payload Too Large: The uploaded file is too large.';
        break;
      case 404:
        errorMessage = 'Not Found: The requested resource could not be found.';
        break;
      case 500:
        errorMessage = 'Internal Server Error: The server encountered an error and could not complete your request.';
        break;
      case 502:
        errorMessage = 'Bad Gateway: The server received an invalid response from the upstream server.';
        break;
      case 503:
        errorMessage = 'Service Unavailable: The server is currently unable to handle the request due to temporary overload or maintenance.';
        break;
      case 504:
        errorMessage = 'Gateway Timeout: The server did not receive a timely response from the upstream server.';
        break;
      default:
        errorMessage = 'An unexpected error occurred.';
    }
  } else if (error.request) {
    // The request was made but no response was received
    errorMessage = 'No response received from the server.';
  } else {
    // Something happened in setting up the request that triggered an Error
    errorMessage = `Error: ${error.message}`;
  }

  // Kiểm tra xem lỗi đã được hiển thị gần đây chưa
  const errorKey = error.response?.status || error.code || 'unknown';
  const now = Date.now();
  const lastShownTime = recentErrors.get(errorKey + errorMessage);

  if (!lastShownTime || now - lastShownTime > ERROR_COOLDOWN) {
    // Chỉ hiển thị lỗi nếu nó chưa được hiển thị trong khoảng thời gian cooldown
    toast.error(errorMessage);
    recentErrors.set(errorKey + errorMessage, now);
    
    // Xóa lỗi khỏi Map sau khoảng thời gian nhất định để tránh memory leak
    setTimeout(() => {
      recentErrors.delete(errorKey + errorMessage);
    }, ERROR_COOLDOWN + 1000);
  }
};

const refreshAccessToken = async () => {
  try {
    const response = await authApi.post('/auth/refresh-token?type=renew-when-call-api');
    const { accessToken } = response.data;
    return accessToken;
  } catch (error) {
    return Promise.reject(error);
  }
};

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

authApi.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as CustomAxiosRequestConfig;

    if (error.response?.status === 401 && !originalRequest.retry) {
      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshSubscribers.push((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            resolve(authApi(originalRequest));
          });
        });
      }

      originalRequest.retry = true;
      isRefreshing = true;
      try {
        const accessToken = await refreshAccessToken();

        refreshSubscribers.forEach((callback) => callback(accessToken));
        refreshSubscribers = [];
        return await authApi(originalRequest);
      } catch (refreshError) {
        // signOut({ callbackUrl: '/auth/sign-in' });
        return await Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (error.response?.status === 403 && error.response?.statusText === 'Forbidden') {
      // window.location.href = '/403';
    }

    return Promise.reject(error);
  },
);

export default authApi;
